/**
 * Which product/variant the page is showing, mirrored from
 * `window.DUSK.productContext` into Svelte reactivity.
 *
 * A module-level singleton initialised from `theme.ts`, not from an island —
 * islands mount lazily on scroll and would miss earlier changes.
 *
 * @see ADR 0005 for the cross-repo contract.
 */

import { on } from "svelte/events";

export interface ProductContext {
  /** Shopify IDs are strings here — never compare against a numeric literal. */
  productId?: string;
  variantId?: string;
  handle?: string;
  /** Product path, e.g. "/products/foo". */
  url?: string;
}

export const PRODUCT_CHANGED_EVENT = "dusk:product:changed";

export class ProductContextStore {
  current = $state<ProductContext>({});

  #stop: (() => void) | null = null;

  /** Seed from the theme's snapshot, then follow later changes. Idempotent. */
  listen(): void {
    if (this.#stop) return;

    this.#stop = on(document, PRODUCT_CHANGED_EVENT, (event) => {
      const detail = (event as CustomEvent<ProductContext | undefined>).detail;
      if (detail) this.current = { ...detail };
    });

    // Our module entry may run before the theme's deferred scripts, so retry once
    // they have. The listener is attached by then, so a change in between wins.
    // Deferred and module scripts run while readyState is "interactive", so anything
    // short of "complete" can still be ahead of them - "loading" alone misses that case.
    this.#seed();
    if (document.readyState !== "complete") {
      on(document, "DOMContentLoaded", () => this.#seed(), { once: true });
    }
  }

  /** Test hook — islands never stop following the page. */
  stop(): void {
    this.#stop?.();
    this.#stop = null;
  }

  #seed(): void {
    if (Object.keys(this.current).length) return;

    const snapshot = window.DUSK?.productContext;
    if (snapshot && Object.keys(snapshot).length) {
      this.current = { ...snapshot };
    }
  }
}

export const productContextStore = new ProductContextStore();

/**
 * The live context, falling back to the values Liquid rendered into an island's
 * props. Read properties inside `$derived` to track changes.
 *
 * ```svelte
 * const productContext = createProductContext(() => ({ productId: pageProductId }));
 * const itemIds = $derived(productContext.productId ?? pageProductId);
 * ```
 */
export function createProductContext(
  getFallback: () => ProductContext = () => ({}),
): ProductContext {
  productContextStore.listen();

  return {
    get productId() {
      return productContextStore.current.productId ?? getFallback().productId;
    },
    get variantId() {
      return productContextStore.current.variantId ?? getFallback().variantId;
    },
    get handle() {
      return productContextStore.current.handle ?? getFallback().handle;
    },
    get url() {
      return productContextStore.current.url ?? getFallback().url;
    },
  };
}
